import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import type TenantModel from 'garaje/models/tenant';
import type ZoneModel from 'garaje/models/zone';
import { type TenantChangeset } from 'garaje/pods/components/property/settings/tenants/edit-tenant/component';
import type TransitionConfirmService from 'garaje/services/transition-confirm';
import { routeEvent } from 'garaje/utils/decorators/route';
import buildInviteValidations from 'garaje/validations/tenant';

interface EditTenantModel {
  property: ZoneModel;
  tenantChangeset: TenantChangeset;
}

export default class PropertySettingsTenantsEditRoute extends Route {
  @service declare store: Store;
  @service declare transitionConfirm: TransitionConfirmService;

  async model(params: { tenant_id: string }): Promise<EditTenantModel> {
    const property = <ZoneModel>this.modelFor('property');
    const tenant = await this.store.findRecord('tenant', params.tenant_id);

    const validations = buildInviteValidations();

    const validator = lookupValidator(validations);

    return {
      property,
      tenantChangeset: <TenantChangeset>Changeset(tenant, validator, validations),
    };
  }

  @routeEvent
  routeWillChange(transition: Transition): void {
    const { tenantChangeset } = <EditTenantModel>this.modelFor(this.routeName);
    const noChanges = !tenantChangeset?.isDirty;

    if (transition.to.name === this.routeName || noChanges) return;

    void this.transitionConfirm.displayConfirmTask.perform(transition, {
      continue: () => {
        this.#cleanup(tenantChangeset);
        tenantChangeset.rollback();
      },
    });
  }

  #cleanup(tenant: TenantModel): void {
    tenant.suites.filter((suite) => suite.isNew).forEach((suite) => suite.rollbackAttributes());
  }
}
